/**
 * LevelIndicator — Replica Progression Display
 * 
 * Shows the replica's current level, progress to next level,
 * and a user-controlled "Promote" button when ready.
 * 
 * Levels: 🥒 Newborn → 🌱 Apprentice → 🌿 Competent → 🌳 Expert → 👑 Autonomous
 */

import { useState, useEffect } from 'react';
import { TrendingUp, ChevronRight, Award, Lock, CheckCircle } from 'lucide-react';
import { api } from '../../lib/api';

interface LevelDef {
    level: number;
    name: string;
    icon: string;
    description: string;
    minScore: number;
    achieved: boolean;
    current: boolean;
    qualified: boolean;
}

interface LevelData {
    current: {
        level: number;
        name: string;
        icon: string;
        description: string;
        promotedAt: string | null;
    };
    next: {
        level: number;
        name: string;
        icon: string;
        description: string;
        requiredScore: number;
        currentScore: number;
        progress: number;
    } | null;
    readyForPromotion: boolean;
    qualifiedLevel: number;
    allLevels: LevelDef[];
}

interface LevelIndicatorProps {
    replicaId: string;
    compact?: boolean;
    onPromoted?: () => void;
}

export default function LevelIndicator({ replicaId, compact = false, onPromoted }: LevelIndicatorProps) {
    const [levelData, setLevelData] = useState<LevelData | null>(null);
    const [loading, setLoading] = useState(true);
    const [promoting, setPromoting] = useState(false);
    const [promotionResult, setPromotionResult] = useState<string | null>(null);

    useEffect(() => {
        loadLevel();
    }, [replicaId]);

    async function loadLevel() {
        try {
            const res = await api.get<any>(`/cognitive/${replicaId}/level`);
            if (res.data?.level) setLevelData(res.data.level);
        } catch (err) {
            console.error('Failed to load level:', err);
        } finally {
            setLoading(false);
        }
    }

    async function handlePromote() {
        setPromoting(true);
        try {
            const res = await api.post<any>(`/cognitive/${replicaId}/promote`);
            if (res.data?.success) {
                setPromotionResult(res.data.message);
                await loadLevel();
                onPromoted?.();
                // Clear success message after 5s
                setTimeout(() => setPromotionResult(null), 5000);
            }
        } catch (err) {
            console.error('Promotion failed:', err);
        } finally {
            setPromoting(false);
        }
    }

    // Default data when API hasn't responded
    const safeLevel: LevelData = levelData || {
        current: { level: 1, name: 'Newborn', icon: '🥒', description: 'Just created, learning everything', promotedAt: null },
        next: { level: 2, name: 'Apprentice', icon: '🌱', description: 'Basic patterns recognized', requiredScore: 20, currentScore: 0, progress: 0 },
        readyForPromotion: false,
        qualifiedLevel: 1,
        allLevels: [],
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingPulse}>
                    <span style={{ opacity: 0.5, fontSize: 12 }}>Loading level...</span>
                </div>
            </div>
        );
    }

    // Compact mode for dashboard
    if (compact) {
        return (
            <div style={styles.compactContainer}>
                <div style={styles.compactRow}>
                    <span style={styles.compactIcon}>{safeLevel.current.icon}</span>
                    <div style={styles.compactInfo}>
                        <span style={styles.compactLevel}>Level {safeLevel.current.level}</span>
                        <span style={styles.compactName}>{safeLevel.current.name}</span>
                    </div>
                    {safeLevel.next && (
                        <div style={styles.compactProgress}>
                            <div style={styles.compactProgressBar}>
                                <div style={{
                                    ...styles.compactProgressFill,
                                    width: `${safeLevel.next.progress}%`,
                                }} />
                            </div>
                            <span style={styles.compactProgressText}>
                                {Math.round(safeLevel.next.currentScore)}/{safeLevel.next.requiredScore}
                            </span>
                        </div>
                    )}
                </div>
                {safeLevel.readyForPromotion && (
                    <button
                        onClick={handlePromote}
                        disabled={promoting}
                        style={styles.compactPromoteBtn}
                    >
                        <TrendingUp size={12} />
                        {promoting ? 'Promoting...' : `Promote to ${safeLevel.next?.icon} ${safeLevel.next?.name}`}
                    </button>
                )}
            </div>
        );
    }

    // Full view
    return (
        <div style={styles.container}>
            {/* Promotion success banner */}
            {promotionResult && (
                <div style={styles.promotionBanner}>
                    <span>{promotionResult}</span>
                </div>
            )}

            {/* Current Level */}
            <div style={styles.currentLevel}>
                <span style={styles.levelIcon}>{safeLevel.current.icon}</span>
                <div style={styles.levelInfo}>
                    <h4 style={styles.levelTitle}>
                        Level {safeLevel.current.level}: {safeLevel.current.name}
                    </h4>
                    <p style={styles.levelDescription}>{safeLevel.current.description}</p>
                </div>
            </div>

            {/* Progress to Next Level */}
            {safeLevel.next && (
                <div style={styles.progressSection}>
                    <div style={styles.progressHeader}>
                        <span style={styles.progressLabel}>
                            Progress to {safeLevel.next.icon} {safeLevel.next.name}
                        </span>
                        <span style={styles.progressScore}>
                            {Math.round(safeLevel.next.currentScore)} / {safeLevel.next.requiredScore}
                        </span>
                    </div>
                    <div style={styles.progressBar}>
                        <div style={{
                            ...styles.progressFill,
                            width: `${safeLevel.next.progress}%`,
                            backgroundColor: safeLevel.readyForPromotion ? '#34d399' : '#a78bfa',
                        }} />
                    </div>
                </div>
            )}

            {/* Ready for Promotion! */}
            {safeLevel.readyForPromotion && safeLevel.next && (
                <div style={styles.promoteSection}>
                    <div style={styles.readyBadge}>
                        <Award size={16} color="#fbbf24" />
                        <span style={styles.readyText}>Ready for promotion!</span>
                    </div>
                    <button
                        onClick={handlePromote}
                        disabled={promoting}
                        style={{
                            ...styles.promoteButton,
                            opacity: promoting ? 0.7 : 1,
                        }}
                    >
                        <TrendingUp size={16} />
                        <span>
                            {promoting
                                ? 'Promoting...'
                                : `Promote to ${safeLevel.next.icon} ${safeLevel.next.name}`
                            }
                        </span>
                    </button>
                    <p style={styles.promoteHint}>
                        You control when your replica advances. Only you can promote it.
                    </p>
                </div>
            )}

            {/* Level Timeline */}
            <div style={styles.timeline}>
                {safeLevel.allLevels.map((lvl, idx) => (
                    <div key={lvl.level} style={styles.timelineItem}>
                        <div style={{
                            ...styles.timelineDot,
                            backgroundColor: lvl.current
                                ? '#a78bfa'
                                : lvl.achieved
                                    ? '#34d399'
                                    : lvl.qualified
                                        ? '#fbbf24'
                                        : 'rgba(255,255,255,0.1)',
                            boxShadow: lvl.current ? '0 0 8px rgba(167,139,250,0.5)' : 'none',
                        }}>
                            {lvl.achieved && !lvl.current && <CheckCircle size={10} color="#fff" />}
                            {lvl.current && <span style={{ fontSize: 10 }}>{lvl.icon}</span>}
                            {!lvl.achieved && !lvl.qualified && <Lock size={8} color="rgba(255,255,255,0.3)" />}
                            {lvl.qualified && !lvl.achieved && <TrendingUp size={8} color="#fff" />}
                        </div>
                        {idx < safeLevel.allLevels.length - 1 && (
                            <div style={{
                                ...styles.timelineLine,
                                backgroundColor: lvl.achieved ? '#34d399' : 'rgba(255,255,255,0.08)',
                            }} />
                        )}
                        <span style={{
                            ...styles.timelineLabel,
                            color: lvl.current ? '#a78bfa' : lvl.achieved ? '#34d399' : 'rgba(255,255,255,0.3)',
                            fontWeight: lvl.current ? 700 : 400,
                        }}>
                            {lvl.name}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================
// STYLES
// ============================================

const styles: Record<string, React.CSSProperties> = {
    container: {
        background: 'linear-gradient(135deg, rgba(15,15,25,0.95), rgba(20,15,35,0.95))',
        borderRadius: 16,
        border: '1px solid rgba(167,139,250,0.12)',
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    loadingPulse: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 0',
    },
    promotionBanner: {
        background: 'linear-gradient(135deg, rgba(52,211,153,0.15), rgba(167,139,250,0.15))',
        border: '1px solid rgba(52,211,153,0.3)',
        borderRadius: 12,
        padding: '12px 16px',
        textAlign: 'center' as const,
        fontSize: 15,
        fontWeight: 600,
        color: '#34d399',
    },
    currentLevel: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
    },
    levelIcon: {
        fontSize: 36,
    },
    levelInfo: {
        flex: 1,
    },
    levelTitle: {
        fontSize: 16,
        fontWeight: 700,
        color: '#fff',
        margin: 0,
    },
    levelDescription: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.5)',
        margin: '4px 0 0',
    },
    progressSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    progressHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    progressLabel: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.6)',
    },
    progressScore: {
        fontSize: 12,
        fontWeight: 700,
        color: '#a78bfa',
    },
    progressBar: {
        height: 6,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        borderRadius: 3,
        transition: 'width 0.5s ease',
    },
    promoteSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: '12px 0',
    },
    readyBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 12px',
        borderRadius: 20,
        background: 'rgba(251,191,36,0.1)',
        border: '1px solid rgba(251,191,36,0.2)',
    },
    readyText: {
        fontSize: 12,
        fontWeight: 600,
        color: '#fbbf24',
    },
    promoteButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '12px 24px',
        borderRadius: 12,
        border: 'none',
        background: 'linear-gradient(135deg, #34d399, #059669)',
        color: '#fff',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 16px rgba(52,211,153,0.3)',
    },
    promoteHint: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.3)',
        fontStyle: 'italic',
        margin: 0,
        textAlign: 'center' as const,
    },
    timeline: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingTop: 10,
        borderTop: '1px solid rgba(255,255,255,0.04)',
    },
    timelineItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        position: 'relative' as const,
        flex: 1,
    },
    timelineDot: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
    },
    timelineLine: {
        position: 'absolute' as const,
        top: 12,
        left: '60%',
        right: '-40%',
        height: 2,
    },
    timelineLabel: {
        fontSize: 9,
        textTransform: 'uppercase' as const,
        letterSpacing: 0.5,
    },
    // Compact styles
    compactContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '8px 0',
    },
    compactRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    compactIcon: {
        fontSize: 22,
    },
    compactInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    },
    compactLevel: {
        fontSize: 9,
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase' as const,
        letterSpacing: 0.5,
    },
    compactName: {
        fontSize: 13,
        fontWeight: 700,
        color: '#a78bfa',
    },
    compactProgress: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 3,
    },
    compactProgressBar: {
        width: '100%',
        maxWidth: 80,
        height: 4,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.08)',
        overflow: 'hidden',
    },
    compactProgressFill: {
        height: '100%',
        borderRadius: 2,
        background: '#a78bfa',
        transition: 'width 0.3s ease',
    },
    compactProgressText: {
        fontSize: 9,
        color: 'rgba(255,255,255,0.4)',
    },
    compactPromoteBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 8,
        border: 'none',
        background: 'linear-gradient(135deg, #34d399, #059669)',
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
    },
};
